import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import LocalizedLink from "@/components/LocalizedLink";
import { ArrowRight } from "lucide-react";
import cardBronze from "@/assets/card-bronze.png";
import cardGold from "@/assets/card-gold.png";
import cardPlatinum from "@/assets/card-platinum.png";
import cardDiamond from "@/assets/card-diamond.png";
import cardReserve from "@/assets/card-reserve.png";

const tiers = [
  { key: "bronze", image: cardBronze, accent: "border-ancient-pottery" },
  { key: "gold", image: cardGold, accent: "border-pagoda-gold" },
  { key: "platinum", image: cardPlatinum, accent: "border-silver-jewel" },
  { key: "diamond", image: cardDiamond, accent: "border-royal-blue" },
  { key: "reserve", image: cardReserve, accent: "border-burgundy-gem" },
] as const;

const MembershipSection = () => {
  const { t } = useTranslation();

  return (
    <section id="membership" className="py-20 lg:py-28 bg-secondary">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <span className="text-xs font-body font-semibold tracking-[0.2em] uppercase text-accent mb-3 block">
            {t("memberships.badge")}
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-display font-bold text-foreground mb-4">
            {t("memberships.title")}
          </h2>
          <p className="text-base text-muted-foreground font-body">
            {t("memberships.subtitle")}
          </p>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6">
          {tiers.map((tier, i) => (
            <motion.div
              key={tier.key}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
              className="bg-card rounded-lg overflow-hidden shadow-card hover:shadow-elegant transition-all group border border-border hover:border-accent/30"
            >
              <div className="p-4 flex justify-center bg-secondary/50">
                <img
                  src={tier.image}
                  alt={t(`memberships.tiers.${tier.key}.name`)}
                  className="h-36 object-contain group-hover:scale-105 transition-transform duration-500"
                  width={280}
                  height={144}
                  loading="lazy"
                  decoding="async"
                />
              </div>
              <div className="p-5">
                <h3 className="text-lg font-display font-bold text-foreground mb-1">
                  {t(`memberships.tiers.${tier.key}.name`)}
                </h3>
                <p className="text-xs font-body text-muted-foreground mb-2">
                  {t(`memberships.tiers.${tier.key}.duration`)}
                </p>
                <p className="text-lg font-display font-bold text-accent mb-3">
                  {t(`memberships.tiers.${tier.key}.price`)}
                </p>
                <p className="text-xs text-muted-foreground font-body leading-relaxed mb-4">
                  {t(`memberships.tiers.${tier.key}.description`)}
                </p>
                <LocalizedLink
                  to={`/memberships#${tier.key}`}
                  className="inline-flex items-center gap-1 text-sm font-body font-semibold text-accent hover:underline"
                >
                  {t("memberships.learnMore")}
                  <ArrowRight className="h-3 w-3" />
                </LocalizedLink>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default MembershipSection;
